/**
 * domains/education/copy/educationExamCopy.js
 *
 * Template-aware wording for the shared education pages.
 *
 * The Exam Portal template reuses the same live pages as the Default template (my courses,
 * purchase summary, course detail …), but its candidates sit *exams*, not courses. Rather than
 * forking those pages, callers pass their user-facing strings through `examCopy`, which rewrites
 * the vocabulary only when the Exam template is active. The Default template gets the original
 * string back, unchanged and unmeasurably cheap.
 *
 * Usage:
 *   const copy = useEducationExamCopy();
 *   <h1>{copy('My Enrollments')}</h1>   // Exam template → "My Exams"
 */

import { useCallback } from 'react';

import { useActiveEducationTheme } from '../../../template/EducationThemeBridge.jsx';

/**
 * Whole phrases are replaced before individual words, so headings read naturally instead of
 * being word-substituted into something clumsy ("My Enrollments" → "My Exams", not
 * "My Registrations").
 */
const PHRASES = [
  ['My Enrollments', 'My Exams'],
  ['my enrollments', 'my exams'],
  ['All Courses', 'All Exams'],
  ['all courses', 'all exams'],
  ['My Courses', 'My Exams'],
  ['Your Learning Progress', 'Your Exam Progress'],
  ['Continue your learning journey', 'Continue your exam preparation'],
  ['continue your learning journey', 'continue your exam preparation'],
  ['Track your progress and continue your learning journey', 'Track your attempts and continue your exam preparation'],
  ['View Course Details', 'View Exam Details'],
  ['View course details, curriculum, and materials', 'View exam details, blueprint, and materials'],
  ['Choose Schedule', 'Choose Exam Slot'],
  ['Enrollment Cancelled', 'Registration Cancelled'],
  ['Cancel Enrollment', 'Cancel Registration'],
  ['Review cancelled enrollments', 'Review cancelled registrations'],
  ['View all your enrolled courses', 'View all your registered exams'],
  ['Review your completed courses', 'Review your completed exams'],
  ['Courses that are coming soon', 'Exams that are coming soon'],
  ['Need help with your courses?', 'Need help with your exams?'],
  ['Total orders placed for courses', 'Total orders placed for exams'],
  ['View detailed information about your course purchases, invoices, and order history.', 'View detailed information about your exam purchases, invoices, and order history.'],
  ['Explore our catalog to find new courses to enroll in.', 'Explore our catalog to find new exams to register for.'],
  ['Start learning to see your in-progress courses here.', 'Start your exam to see your in-progress exams here.'],
  ['Complete courses to see them in your achievements.', 'Complete exams to see them in your achievements.'],
  ['Cancelled enrollments will appear here.', 'Cancelled exam registrations will appear here.'],
  ['Upcoming enrollments will appear here.', 'Upcoming exams will appear here.'],
  ['Browse Courses', 'Browse Exams'],
  ['Keep Enrollment', 'Keep Registration'],
  ['Enrolled on:', 'Registered on:'],
];

/** Case-preserving single words, applied on word boundaries so "coursework" is left alone. */
const WORDS = [
  [/\bCourses\b/g, 'Exams'],
  [/\bcourses\b/g, 'exams'],
  [/\bCourse\b/g, 'Exam'],
  [/\bcourse\b/g, 'exam'],
  [/\bEnrollments\b/g, 'Registrations'],
  [/\benrollments\b/g, 'registrations'],
  [/\bEnrollment\b/g, 'Registration'],
  [/\benrollment\b/g, 'registration'],
  [/\bEnrolled\b/g, 'Registered'],
  [/\benrolled\b/g, 'registered'],
];

/** Rewrites one string into Exam-template vocabulary. Non-strings pass straight through. */
export const examCopy = (text) => {
  if (typeof text !== 'string' || !text) return text;

  let output = text;
  PHRASES.forEach(([from, to]) => {
    if (output.includes(from)) output = output.split(from).join(to);
  });
  WORDS.forEach(([pattern, replacement]) => {
    output = output.replace(pattern, replacement);
  });
  return output;
};

/**
 * @returns {(text: string) => string} identity on the Default template, Exam vocabulary on the
 *   Exam template.
 */
export const useEducationExamCopy = () => {
  const { isExamTemplate } = useActiveEducationTheme();
  return useCallback((text) => (isExamTemplate ? examCopy(text) : text), [isExamTemplate]);
};

export default useEducationExamCopy;
